import React from 'react';
import { Instagram, Music, ExternalLink, Award, TrendingUp, Youtube } from 'lucide-react';
import SpotifyEmbed from '../SpotifyEmbed';

const APPLE_MUSIC_URL = 'https://music.apple.com/us/artist/%C3%B2lah-bliss/980931404';

export const PageHeader = () => (
  <div className="mb-16 text-center">
    <div className="flex justify-center items-center gap-6 mb-6">
      <img
        src="/rhodium-logo.png"
        alt="Rhodium Records"
        className="logo-img h-24 w-24 md:h-32 md:w-32 object-contain"
        style={{ animation: 'pulse 3s ease-in-out infinite' }}
      />
    </div>
    <h1 className="text-5xl md:text-7xl font-black mb-4">
      <span className="text-white">Element </span>
      <span className="logo-text">45</span>
    </h1>
    <p className="text-white/60 text-lg max-w-2xl mx-auto">
      Rhodium Records' experimental imprint dedicated to pushing sonic boundaries
    </p>
  </div>
);

const SocialLink = ({ href, icon: Icon, label }) => (
  <a
    href={href}
    className="flex items-center gap-2 px-4 py-2 bg-silver-400/10 hover:bg-silver-400/20 border border-silver-400/30 rounded-lg text-silver-400 text-sm transition-all"
  >
    <Icon size={16} />
    {label}
  </a>
);

export const FeaturedArtistCard = ({ artist, onPlay }) => (
  <div className="mb-20">
    <div className="bg-gradient-to-r from-silver-900/20 to-transparent border border-silver-400/20 rounded-2xl overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
        <div className="relative h-96 md:h-auto overflow-hidden">
          <img src={artist.image} alt={artist.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          {artist.releases?.length > 0 && (
            <button
              onClick={() => onPlay(artist.releases[0])}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-silver-400 hover:bg-silver-300 flex items-center justify-center transition-all duration-300 hover:scale-110"
            >
              <Music size={36} className="text-black ml-1" />
            </button>
          )}
        </div>

        <div className="p-8 md:p-12 flex flex-col justify-center">
          <div className="mb-6">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-3">{artist.name}</h2>
            <p className="text-silver-400 text-lg font-medium mb-6">{artist.genre}</p>
            <p className="text-white/80 text-base leading-relaxed mb-6">{artist.bio}</p>
            <p className="text-white/70 text-sm leading-relaxed">{artist.fullBio}</p>
          </div>

          <div className="flex gap-4 mb-8 flex-wrap">
            <SocialLink href={artist.social.instagram} icon={Instagram} label="Instagram" />
            <SocialLink href={artist.social.appleMusic} icon={Music} label="Apple Music" />
            <SocialLink href={artist.social.spotify} icon={ExternalLink} label="Spotify" />
            <SocialLink href={artist.social.youtube} icon={Youtube} label="YouTube" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const AchievementsSection = ({ achievements }) => (
  <div className="mb-20">
    <h3 className="text-3xl md:text-4xl font-bold text-white mb-8 flex items-center gap-3">
      <Award className="text-silver-400" size={32} />
      Achievements
    </h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {achievements.map((achievement) => (
        <div
          key={achievement}
          className="bg-silver-900/10 border border-silver-900/20 hover:border-silver-400/30 rounded-xl p-6 transition-all duration-300 hover:transform hover:scale-105"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-silver-400/20 flex items-center justify-center flex-shrink-0">
              <TrendingUp size={20} className="text-silver-400" />
            </div>
            <p className="text-white/80 leading-relaxed">{achievement}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const ReleasesSection = ({ artist }) => (
  <div className="mb-20">
    <h3 className="text-3xl md:text-4xl font-bold text-white mb-8">
      <span className="text-white">Featured </span>
      <span className="text-silver-400">Releases</span>
    </h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {artist.releases.map((release) => (
        <div
          key={release.id}
          className="bg-silver-900/10 rounded-xl overflow-hidden border border-silver-900/20 hover:border-silver-400/30 transition-all duration-300"
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-white font-bold text-xl mb-2">{release.title}</h4>
                <p className="text-silver-400 text-sm mb-1">{release.year}</p>
                <span className="inline-block px-3 py-1 bg-silver-400/10 border border-silver-400/30 rounded-full text-silver-400 text-xs font-bold uppercase tracking-wider">
                  {release.type}
                </span>
              </div>
            </div>

            <p className="text-white/70 text-sm leading-relaxed mb-6">{release.description}</p>

            <div className="mb-4">
              <SpotifyEmbed
                url={release.spotifyUrl}
                title={`${release.title} — ${artist.name}`}
                artwork={release.artwork}
                height={152}
              />
            </div>

            <a
              href={release.spotifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full px-4 py-3 bg-silver-400 hover:bg-silver-300 text-black font-bold text-sm uppercase tracking-wider rounded-lg transition-colors text-center"
            >
              Open in Spotify
            </a>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const MediaSection = ({ icon: Icon, title, children, ctaHref, ctaLabel }) => (
  <div className="mb-20">
    <h3 className="text-3xl md:text-4xl font-bold text-white mb-8 flex items-center gap-3">
      <Icon className="text-silver-400" size={32} />
      {title}
    </h3>
    <div className="bg-silver-900/10 border border-silver-400/20 rounded-2xl p-8">
      <div className="max-w-4xl mx-auto">
        {children}
        <div className="mt-6 text-center">
          <a
            href={ctaHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-silver-400 hover:bg-silver-300 text-black font-bold text-sm uppercase tracking-wider rounded-lg transition-all duration-300 hover:scale-105"
          >
            <Icon size={20} />
            {ctaLabel}
          </a>
        </div>
      </div>
    </div>
  </div>
);

export const AppleMusicSection = () => (
  <MediaSection icon={Music} title="Listen on Apple Music" ctaHref={APPLE_MUSIC_URL} ctaLabel="Open in Apple Music">
    <iframe
      title="Òlah Bliss on Apple Music"
      allow="autoplay *; encrypted-media *; fullscreen *; clipboard-write"
      frameBorder="0"
      height="450"
      style={{
        width: '100%',
        maxWidth: '660px',
        overflow: 'hidden',
        background: 'transparent',
        margin: '0 auto',
        display: 'block'
      }}
      sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
      src="https://embed.music.apple.com/us/artist/%C3%B2lah-bliss/980931404"
    />
  </MediaSection>
);

export const InstagramFeedSection = () => (
  <MediaSection
    icon={Instagram}
    title="Follow on Instagram"
    ctaHref="https://www.instagram.com/olahbliss/"
    ctaLabel="Follow @olahbliss"
  >
    <div className="aspect-square max-w-xl mx-auto">
      <iframe
        title="Òlah Bliss Instagram"
        src="https://www.instagram.com/olahbliss/embed"
        className="w-full h-full rounded-lg"
        frameBorder="0"
        scrolling="no"
        style={{ background: 'white' }}
      />
    </div>
  </MediaSection>
);

export const YouTubeSection = () => (
  <MediaSection
    icon={Youtube}
    title="Watch on YouTube"
    ctaHref="https://www.youtube.com/@olahbliss"
    ctaLabel="Subscribe on YouTube"
  >
    <div className="aspect-video rounded-lg overflow-hidden bg-black">
      <iframe
        src="https://www.youtube.com/embed/videoseries?list=UUpsnliGSysPbq77RQA8TcOg"
        className="w-full h-full"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title="Òlah Bliss YouTube Channel"
      />
    </div>
  </MediaSection>
);

export const AboutElement45 = () => (
  <div className="text-center">
    <div className="inline-block bg-silver-900/20 border border-silver-400/20 rounded-2xl p-12">
      <div className="flex justify-center mb-6">
        <img src="/rhodium-logo.png" alt="Rhodium Records" className="h-16 w-16 object-contain" />
      </div>
      <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">About Element 45</h3>
      <p className="text-white/70 max-w-2xl leading-relaxed">
        Element 45 is Rhodium Records' dedicated experimental division, named after the atomic number
        of rhodium itself. We curate and champion artists who dare to venture beyond conventional
        musical boundaries, creating immersive sonic experiences that challenge and inspire.
        Each Element 45 release represents a commitment to artistic excellence and innovation.
      </p>
    </div>
  </div>
);
